import { useState } from 'react';
import {
  Box, TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText,
  InputAdornment, IconButton
} from '@mui/material';
import CustomModal from 'src/components/modal/customModal';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import LoadingButton from '@mui/lab/LoadingButton';
import { styled, useTheme } from '@mui/material/styles';
import { SMTP_TYPE } from 'src/lib/Enums';
import { checkButtonAccess } from 'src/utils/common';
import { axiosPostCall } from 'src/services/apisCall';

const RowTow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '20px',
  alignItems: 'center',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const AddUpdateSmtp = ({ openModal, forceLoad, loadit, handleCloseModal, title, edit, data }) => {
  const theme = useTheme();
  const inputSx = {
    color: theme.palette.text.primary,
    borderRadius: "6px",
    height: '55px'
  };

  const [user, setUser] = useState(edit ? data?.user : '');
  const [host, setHost] = useState(edit ? data?.host : '');
  const [password, setPassword] = useState(edit ? data?.password : '');
  const [port, setPort] = useState(edit ? data?.port : '');
  const [mailFrom, setmailFrom] = useState(edit ? data?.mailFrom : '');
  const [type, setType] = useState(edit ? data?.type : '');
  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState({});

  const handleChange = async (e) => {
    e.preventDefault();
    const errorTemp = {};
    if (!type) errorTemp.type = "Type is required";
    if (!user) errorTemp.user = "User is required";
    if (!host) errorTemp.host = "Host is required";
    if (!password) errorTemp.password = "Password is required";
    if (!port) errorTemp.port = "Port is required";
    if (!mailFrom) {
      errorTemp.mailFrom = "Mail From is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mailFrom)) {
      errorTemp.mailFrom = "Mail From must be a valid email address";
    }

    setError(errorTemp);
    if (Object.keys(errorTemp).length === 0) {
      try {
        const { message } = await axiosPostCall("addSmtp", {
          user: user.trim(),
          type,
          host: host.trim(),
          pass: password,
          port: port.trim(),
          mailFrom: mailFrom.trim()
        });
        renderSuccessMessage(message);
        handleCloseModal();
        forceLoad(!loadit);

        if (!edit) {
          setType('');
          setUser('');
          setPassword('');
          setHost('');
          setPort('');
          setmailFrom('');
        }
        setError({});
      } catch (error) {
        renderErrorMessage(error?.message);
      }
    }
  };

  const description = (
    <Box
      component="form"
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '25px', width: '100%', padding: '25px' }}
      noValidate
      autoComplete="off"
    >
      <RowTow>
        <FormControl fullWidth error={!!error.type}>
          <InputLabel id="smtp-type-label">Smtp Type</InputLabel>
          <Select
            labelId="smtp-type-label"
            value={type}
            label="Smtp Type"
            onChange={(e) => setType(e.target.value)}
            name="type"
          >
            {Object.entries(SMTP_TYPE).map(([code, smtpType]) => (
              <MenuItem key={code} value={code}>{smtpType}</MenuItem>
            ))}
          </Select>
          {error.type && <FormHelperText>{error.type}</FormHelperText>}
        </FormControl>
      </RowTow>

      <RowTow>
        <FormControl fullWidth error={!!error.host}>
          <TextField
            fullWidth
            label="Host"
            type="text"
            variant="outlined"
            InputProps={{ sx: inputSx }}
            value={host}
            onChange={(e) => setHost(e.target.value)}
            size="small"
          />
          {error.host && <FormHelperText>{error.host}</FormHelperText>}
        </FormControl>

        <FormControl fullWidth error={!!error.user}>
          <TextField
            fullWidth
            label="User"
            type="text"
            variant="outlined"
            InputProps={{ sx: inputSx }}
            value={user}
            onChange={(e) => setUser(e.target.value)}
            size="small"
          />
          {error.user && <FormHelperText>{error.user}</FormHelperText>}
        </FormControl>
      </RowTow>

      <RowTow>
        <FormControl fullWidth error={!!error.password}>
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            InputProps={{
              sx: inputSx,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="small"
          />
          {error.password && <FormHelperText>{error.password}</FormHelperText>}
        </FormControl>
      </RowTow>

      <RowTow>
        <FormControl fullWidth error={!!error.port}>
          <TextField
            fullWidth
            label="Port"
            type="text"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            variant="outlined"
            InputProps={{ sx: inputSx }}
            value={port}
            onChange={(e) => setPort(e.target.value)}
            size="small"
          />
          {error.port && <FormHelperText>{error.port}</FormHelperText>}
        </FormControl>

        <FormControl fullWidth error={!!error.mailFrom}>
          <TextField
            fullWidth
            label="Mail From"
            type="text"
            variant="outlined"
            InputProps={{ sx: inputSx }}
            value={mailFrom}
            onChange={(e) => setmailFrom(e.target.value)}
            size="small"
          />
          {error.mailFrom && <FormHelperText>{error.mailFrom}</FormHelperText>}
        </FormControl>
      </RowTow>

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', pt: '10px', pb: '20px' }}>
        <LoadingButton
          variant="outlined"
          onClick={handleChange}
          disabled={!checkButtonAccess("updateServer", "w")}
        >
          {edit ? "Update" : "Create"}
        </LoadingButton>
      </Box>
    </Box>
  );

  return openModal && (
    <CustomModal
      open={openModal}
      title={title}
      description={description}
      close={handleCloseModal}
      isCloseIcon={true}
    />
  );
};

export default AddUpdateSmtp;
